/**
 * Shared timeline rendering components
 */

import { useMemo } from 'react';
import { SwimLane } from './SwimLane';
import { formatYear, generateYearTicks, MIN_YEAR, MAX_YEAR, type TimelineEvent } from './timeline-utils';
import { cn } from '@/lib/utils';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';

interface TimelineSwimLanesProps {
    swimLanes: Array<{
        id: string;
        label: string;
        icon: string;
        color: string;
        isSubcategory?: boolean;
        parentLabel?: string;
        parentLaneId?: string;
    }>;
    eventsByLane: Map<string, TimelineEvent[][]>;
    visibleSwimLanes: Set<string>;
    handleSwimLaneToggle: (laneId: string) => void;
    timelineWidth: number;
    visibleStartYear: number;
    visibleEndYear: number;
    onEventClick: (event: TimelineEvent) => void;
}

/**
 * Renders all swimlanes with their events
 */
export function TimelineSwimLanes({
    swimLanes,
    eventsByLane,
    visibleSwimLanes,
    handleSwimLaneToggle,
    timelineWidth,
    visibleStartYear,
    visibleEndYear,
    onEventClick
}: TimelineSwimLanesProps) {
    return (
        <div className="flex flex-col flex-1">
            {swimLanes.map(lane => {
                const laneEvents = eventsByLane.get(lane.id) || [];

                // Only hide subcategory lanes with no events
                // Always show parent lanes (even if empty)
                if (lane.isSubcategory && laneEvents.length === 0) {
                    return null;
                }

                // Each lane (including subcategories) has its own visibility state
                const isLaneVisible = visibleSwimLanes.has(lane.id);

                // Pass empty events array if lane is not visible (hide events but keep lane header)
                const visibleEvents = isLaneVisible ? laneEvents : [];

                return (
                    <SwimLane
                        key={lane.id}
                        label={lane.label}
                        icon={lane.icon}
                        color={lane.color}
                        eventRows={visibleEvents}
                        minYear={MIN_YEAR}
                        maxYear={MAX_YEAR}
                        timelineWidth={timelineWidth}
                        onEventClick={onEventClick}
                        isSubcategory={lane.isSubcategory}
                        parentLabel={lane.parentLabel}
                        isVisible={isLaneVisible}
                        onToggleVisibility={() => handleSwimLaneToggle(lane.id)}
                        visibleStartYear={visibleStartYear}
                        visibleEndYear={visibleEndYear}
                    />
                );
            })}
        </div>
    );
}

interface TimelineVerticalTicksProps {
    timelineWidth: number;
    visibleStartYear: number;
    visibleEndYear: number;
}

/**
 * Renders vertical tick lines extending through swimlanes
 */
export function TimelineVerticalTicks({
    timelineWidth,
    visibleStartYear,
    visibleEndYear
}: TimelineVerticalTicksProps) {
    const yearTicks = useMemo(() => {
        return generateYearTicks(MIN_YEAR, MAX_YEAR, timelineWidth, visibleStartYear, visibleEndYear);
    }, [timelineWidth, visibleStartYear, visibleEndYear]);

    return (
        <div className="absolute top-0 bottom-0 left-[60px] sm:left-[232px] pointer-events-none z-5">
            <div className="relative h-full" style={{ width: `${timelineWidth}px` }}>
                {yearTicks.map((tick, index) => (
                    <div
                        key={`vertical-${index}`}
                        className={`absolute top-0 bottom-0 -translate-x-1/2 ${tick.isMajor
                            ? 'border-l border-[#5c4d3c]/10'
                            : 'border-l border-[#c9b896]/5'
                            }`}
                        style={{ left: `${tick.position}px` }}
                    />
                ))}
            </div>
        </div>
    );
}

interface TimelineYearAxisProps {
    timelineWidth: number;
    visibleStartYear: number;
    visibleEndYear: number;
    children?: React.ReactNode; // For passage date display or other overlays
}

/**
 * Renders the year axis at the bottom of the timeline
 */
export function TimelineYearAxis({
    timelineWidth,
    visibleStartYear,
    visibleEndYear,
    children
}: TimelineYearAxisProps) {
    const isDarkMode = useDarkMode();

    const yearTicks = useMemo(() => {
        return generateYearTicks(MIN_YEAR, MAX_YEAR, timelineWidth, visibleStartYear, visibleEndYear);
    }, [timelineWidth, visibleStartYear, visibleEndYear]);

    return (
        <div className={cn(
            "sticky bottom-0 z-40 border-t-2",
            isDarkMode ? "bg-[#1a1510] border-[#5c4d3c]" : "bg-[#f4f1ea] border-[#a89575]"
        )}>
            <div className="relative h-14 ml-[60px] sm:ml-[232px]">
                <div
                    className={cn("relative h-full", isDarkMode ? 'bg-[#1a1510]' : 'bg-white')}
                    style={{ width: `${timelineWidth}px`, transition: 'none' }}
                >
                    {yearTicks.map((tick, index) => (
                        <div
                            key={index}
                            className="absolute top-0"
                            style={{ left: `${tick.position}px`, transition: 'none' }}
                        >
                            {/* Tick Line */}
                            <div
                                className={cn(
                                    "absolute top-0 left-0 -translate-x-1/2",
                                    tick.isMajor
                                        ? (isDarkMode ? 'h-8 border-l-2 border-[#c9b896]' : 'h-8 border-l-2 border-[#5c4d3c]')
                                        : tick.isMicro
                                            ? (isDarkMode ? 'h-2 border-l border-[#5c4d3c]' : 'h-2 border-l border-[#c9b896]')
                                            : (isDarkMode ? 'h-4 border-l border-[#a89575]' : 'h-4 border-l border-[#a89575]')
                                )}
                            />

                            {/* Tick Label */}
                            {tick.showLabel && tick.isMajor && (
                                <div className={cn(
                                    "absolute top-9 left-0 -translate-x-1/2 whitespace-nowrap text-sm font-bold",
                                    isDarkMode ? "text-[#c9b896]" : "text-[#5c4d3c]"
                                )}>
                                    {formatYear(tick.year)}
                                </div>
                            )}
                            {tick.showLabel && !tick.isMajor && !tick.isMicro && (
                                <div className={cn(
                                    "absolute top-[18px] left-0 -translate-x-1/2 whitespace-nowrap text-xs font-medium",
                                    isDarkMode ? "text-[#a89575]/80" : "text-[#a89575]"
                                )}>
                                    {Math.abs(tick.year)}
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Optional children (e.g., passage date display) */}
                    {children}
                </div>
            </div>
        </div>
    );
}

